<template>
  <div v-if="locationName === '' || locationName == null || false">
    <div class="row">
      <div class="col-12">
        <h5>Dieser Kunde hat keinen Standort.</h5>
        <p>Sie müssen den Standort auswählen, wenn Sie Maßnahmen hinzufügen oder ändern möchten</p>
      </div>
    </div>
  </div>
  <div v-else>
    <form v-if="isLoaded" @submit.prevent="submitForm" novalidate>
      <div class="row">
        <div class="col-12">
          <h6 class="mb-4">Standort: {{ locationName }}</h6>

          <AuditInfo
              :user-role="currentUserRole"
              :created-by-user-data="formData.createdByUserData"
              :created-at="formData.createdAt"
              :updated-by-user-data="formData.updatedByUserData"
              :updated-at="formData.updatedAt"/>

          <div v-if="
              formData.unemployedId === '' ||
              formData.unemployedId == null ||
               false">
            <h6 class="mb-4" style="color: red">{{ $t('general.error') }}</h6>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <SelectVue v-model="formData.courseId"
                     :options="courseDropdown"
                     :required="true"
                     label="Bezeichnung"
                     placeholder="Leistungsart auswählen"
                     :validation="v$.courseId"
                     :messages="v$.courseId.required.$message"
                     @update:modelValue="onChangeCourseId"/>

          <SelectVue v-model="formData.clientId"
                     :options="clientDropdown"
                     :required="true"
                     label="Kostenträger"
                     placeholder="Wählen Sie Kostenträger"
                     :validation="v$.clientId"
                     :messages="v$.clientId.required.$message"/>
        </div>
      </div>
      <div class="row">
        <div class="col-lg-6 col-md-12 col-sm-12">
          <Datepicker
              v-model="formData.startDate"
              :required="true"
              label="Start"
              position="down"
              :validation="v$.startDate"
              :messages="v$.startDate.required.$message"/>

          <div v-if="billingTypeDropdown && billingTypeDropdown.length">
            <SelectVue v-model="formData.billingType"
                       :options="billingTypeDropdown"
                       :required="true"
                       label="Art der Abrechnung"
                       placeholder="Art der Abrechnung"
                       :validation="v$.billingType"
                       :messages="v$.billingType.required.$message"/>
          </div>
        </div>
        <div class="col-lg-6 col-md-12 col-sm-12">
          <Datepicker
              v-model="formData.endDate"
              :required="true"
              label="Ende"
              position="down"
              :validation="v$.endDate"
              :messages="v$.endDate.required.$message"/>

          <BaseInput v-model="formData.clerk"
                     label="Sachbearbeiter"/>
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <div v-if="moduleDropdown && moduleDropdown.length">
            <SelectVue v-model="formData.moduleIds"
                       :options="moduleDropdown"
                       :is-clear="true"
                       :is-multi="true"
                       :required="true"
                       label="Module"
                       positionType="top"
                       :validation="v$.moduleIds"
                       :messages="v$.moduleIds?.required?.$message"/>
          </div>
        </div>
      </div>
      <div class="d-flex justify-content-center w-100 mt-3">
        <SaveButton
            :label="$t('general.button.save')"
            :loading="isSubmitting"/>
      </div>
    </form>
  </div>
</template>

<script setup>
import coursePrivateLearningCoachingService from '@/api/Course/PrivateLearningCoaching';
import privateLearningCoachingService from '@/api/Unemployed/PrivateLearningCoaching';
import AuditInfo from '@/components/AuditInfo.vue';
import BaseInput from '@/components/base/BaseInput.vue';
import Datepicker from '@/components/picker/Datepicker.vue';
import SelectVue from '@/components/SelectVue.vue';
import { CloseToast, ErrorToast, SuccessToast } from '@/components/VueToast';
import { getCoursePrivateLearningCoachingDropdown } from '@/shared/dropdowns';
import { requiredRule } from '@/shared/validators/validationRules';
import { piniaEnumsStore } from '@/store/piniaEnumsStore';
import { piniaUserStore } from '@/store/piniaUserStore';
import useVuelidate from '@vuelidate/core';
import { computed, defineEmits, defineProps, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import SaveButton from '@/components/button/SaveButton.vue';

const { t }               = useI18n();
const piniaEnumsState     = piniaEnumsStore();
const getEnums            = computed(() => piniaEnumsState.enums);
const piniaUserState      = piniaUserStore();
const currentUserRole     = computed(() => piniaUserState.currentUser?.user?.roles?.[0]);
const courseDropdown      = ref([]);
const clientDropdown      = ref([]);
const billingTypeDropdown = ref([]);
const moduleDropdown      = ref([]);
const isLoaded            = ref(false);
const isSubmitting        = ref(false);
const emit                = defineEmits([ 'update:modelValue', 'update:submitForm' ]);

const props = defineProps({
  unemployedId: {
    type: Number,
    required: true,
  },
  locationName: {
    type: String,
    required: true,
  },
  locationId: {
    type: Number,
    required: true,
  },
  privateLearningCoachingId: {
    type: Number,
    required: true,
  },
  submitForm: {
    type: Boolean,
    required: true,
    default: false,
  },
});

const formData = ref({
  unemployedId: props.unemployedId, // !!! IMPORTANT
  courseId: null,
  clientId: null,
  startDate: '',
  endDate: '',
  billingType: '',
  moduleIds: [],
});

const formRules = ref({
  unemployedId: requiredRule(t),
  courseId: requiredRule(t),
  clientId: requiredRule(t),
  startDate: requiredRule(t),
  endDate: requiredRule(t),
  billingType: requiredRule(t),
  moduleIds: requiredRule(t),
});

const computedRules = computed(() => formRules.value);
const v$            = useVuelidate(computedRules, formData);

async function submitForm() {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    if (props.privateLearningCoachingId) {
      await privateLearningCoachingService.putPrivateLearningCoaching(
          formData.value, +props.privateLearningCoachingId,
      );
    } else {
      await privateLearningCoachingService.postPrivateLearningCoaching(formData.value);
    }

    SuccessToast(t('general.toasts.save_success'));
    emit('update:modelValue', false);
    emit('update:submitForm', !props.submitForm);
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }
}

async function onChangeCourseId(courseId, coachingData = null) {
  try {
    const { data } = await coursePrivateLearningCoachingService.getCourse(courseId);
    const clients  = data.data?.clients || [];
    const modules  = data.data?.modules || [];

    clientDropdown.value = clients.map(item => ({
      label: `${item.name}`,
      value: item?.id,
    }));

    moduleDropdown.value = modules.map(item => ({
      label: `${item.name}`,
      value: item?.id,
    }));
  } catch (error) {
    if (coachingData) {
      // Course was deleted after being assigned to this coaching - fall back to the
      // data already embedded in the coaching record instead of failing the whole load.
      clientDropdown.value = [];
      moduleDropdown.value = (coachingData.modules || []).map(item => ({
        label: `${item.name}`,
        value: item?.id,
      }));
    } else {
      await ErrorToast(error);
    }
  }
}

function addCourseToDropdown(courseData, dropdown) {
  if (courseData?.id) {
    const course = {
      label: courseData.name,
      value: courseData.id,
    };
    const exists = dropdown.value.some(item => item.value === course.value);
    if (!exists) {
      dropdown.value.push(course);
    }
  }
}

function addClientToDropdown(clientData, dropdown) {
  if (clientData?.id) {
    const client = {
      label: clientData.name,
      value: clientData.id,
    };
    const exists = dropdown.value.some(item => item.value === client.value);
    if (!exists) {
      dropdown.value.push(client);
    }
  }
}

async function getBillingTypeDropdown() {
  billingTypeDropdown.value = getEnums.value?.privateLearningCoachingBillingTypes || [];
}

async function applyPrivateLearningCoachingData(data) {
  try {
    const courseId  = data.data?.course.id;
    const clientId  = data.data?.client.id;
    const modules   = data.data?.modules || [];
    const moduleIds = modules.map(module => module?.id).filter(id => id != null);

    // Add course to dropdown if is logically deleted on backend and is selected for coaching
    addCourseToDropdown(data.data?.course, courseDropdown);

    formData.value = {
      ...data.data,
      unemployedId: props.unemployedId,
      courseId: courseId,
      clientId: clientId,
      moduleIds: moduleIds,
    };

    await onChangeCourseId(courseId, data.data);
    addClientToDropdown(data.data?.client, clientDropdown);
  } catch (error) {
    await ErrorToast(error);
  }
}

onMounted(async () => {
  CloseToast();

  // Kick off in parallel with the dropdown loads below - it doesn't depend on them.
  const coachingDataPromise = props.privateLearningCoachingId
      ? privateLearningCoachingService.getPrivateLearningCoaching(+props.privateLearningCoachingId)
      : null;

  await Promise.all([
    getCoursePrivateLearningCoachingDropdown(
        courseDropdown,
        'name',
        'id',
        props.locationId,
    ),
    getBillingTypeDropdown(),
  ]);

  if (coachingDataPromise) {
    try {
      const {data} = await coachingDataPromise;
      await applyPrivateLearningCoachingData(data);
    } catch (error) {
      await ErrorToast(error);
    }
  }

  isLoaded.value = true;
});


</script>
<style scoped>

</style>